import React from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { SubscriptionStatus, SubscriptionPlanCode } from "common/models/types";
import {
  AdminSubscriptionRow,
  AdminSubscriptionStats,
} from "common/types/adminSubscription";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import Input from "components/atoms/Input";
import { formatKrw } from "utils/subscriptionDisplay";
import SubscriptionTable from "./SubscriptionTable";

interface AdminSubscriptionsTemplateProps {
  rows: AdminSubscriptionRow[]; // 로컬 검색 적용 후 표시용 행
  stats: AdminSubscriptionStats | null;
  statusFilter?: SubscriptionStatus;
  planFilter?: SubscriptionPlanCode;
  search: string;
  selectedId: number | null;
  hasMore: boolean;
  isLoadingMore: boolean;
  onChangeStatus: (status?: SubscriptionStatus) => void;
  onChangePlan: (planCode?: SubscriptionPlanCode) => void;
  onChangeSearch: (value: string) => void;
  onViewRow: (row: AdminSubscriptionRow) => void;
  onLoadMore: () => void;
  onGrant: () => void;
}

const STATUS_OPTIONS: { value: SubscriptionStatus | ""; label: string }[] = [
  { value: "", label: "전체 상태" },
  { value: "ACTIVE", label: "활성" },
  { value: "PENDING", label: "대기" },
  { value: "CANCELLING", label: "해지중" },
  { value: "CANCELLED", label: "해지" },
  { value: "EXPIRED", label: "만료" },
];

const PLAN_OPTIONS: { value: SubscriptionPlanCode | ""; label: string }[] = [
  { value: "", label: "전체 요금제" },
  { value: "PHAI_PREMIUM_MONTHLY", label: "월간" },
  { value: "PHAI_PREMIUM_YEARLY", label: "연간" },
];

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const Select = styled.select`
  height: 40px;
  padding: 0 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
  cursor: pointer;
`;

const SearchWrap = styled.div`
  flex: 1;
  min-width: 200px;
  max-width: 360px;
`;

const StatCardView: React.FC<{ label: string; value: string; sub?: string }> = ({
  label,
  value,
  sub,
}) => (
  <StatCard>
    <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]}>
      {label}
    </Text>
    <Text textType="H3">{value}</Text>
    {sub && (
      <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[500]}>
        {sub}
      </Text>
    )}
  </StatCard>
);

const AdminSubscriptionsTemplate: React.FC<AdminSubscriptionsTemplateProps> = ({
  rows,
  stats,
  statusFilter,
  planFilter,
  search,
  selectedId,
  hasMore,
  isLoadingMore,
  onChangeStatus,
  onChangePlan,
  onChangeSearch,
  onViewRow,
  onLoadMore,
  onGrant,
}) => {
  // 실유료 ACTIVE = byStatus.ACTIVE − comp (이중집계 방지, 통합가이드 §2.2)
  const activeTotal = stats?.byStatus?.ACTIVE ?? 0;
  const compCount = stats?.comp ?? 0;
  const paidActive = Math.max(0, activeTotal - compCount);

  return (
    <div>
      <Header>
        <Text textType="H2">유료 프리미엄 관리</Text>
        <Button
          buttonType="button"
          color="Primary"
          size="Medium"
          type="Text"
          variant="Contained"
          onClick={onGrant}
        >
          무료 프리미엄 제공
        </Button>
      </Header>

      {stats && (
        <StatsRow>
          <StatCardView label="유료 활성" value={`${paidActive}명`} sub={`전체 활성 ${activeTotal}`} />
          <StatCardView label="무료 제공(comp)" value={`${compCount}명`} />
          <StatCardView label="해지" value={`${stats.byStatus?.CANCELLED ?? 0}명`} />
          <StatCardView label="만료" value={`${stats.byStatus?.EXPIRED ?? 0}명`} />
          <StatCardView
            label="월 환산 매출(MRR)"
            value={formatKrw(stats.activeRevenue?.mrr ?? 0)}
            sub={`월 ${stats.activeRevenue?.monthlyCount ?? 0} · 연 ${stats.activeRevenue?.yearlyCount ?? 0}`}
          />
        </StatsRow>
      )}

      <FilterBar>
        <Select
          value={statusFilter ?? ""}
          onChange={(e) =>
            onChangeStatus((e.target.value || undefined) as SubscriptionStatus | undefined)
          }
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
        <Select
          value={planFilter ?? ""}
          onChange={(e) =>
            onChangePlan((e.target.value || undefined) as SubscriptionPlanCode | undefined)
          }
        >
          {PLAN_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
        <SearchWrap>
          <Input
            type="text"
            placeholder="이름 / 이메일 검색 (로드된 목록 내)"
            value={search}
            onChange={(e) => onChangeSearch(e.target.value)}
          />
        </SearchWrap>
      </FilterBar>

      <SubscriptionTable
        rows={rows}
        selectedId={selectedId}
        onViewRow={onViewRow}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={onLoadMore}
      />
    </div>
  );
};

export default AdminSubscriptionsTemplate;
